import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Calendar, Loader2, Mail, ShieldCheck, Globe } from 'lucide-react';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';
import { Switch } from '../../../../components/ui/Switch';
import { PLAN_GROUPS } from '../../../../features/admins/adminsConstants';
import {
  fetchStaffAdminDetail,
  updateStaffAdminPlan,
} from '../../../../features/admins/adminsThunks';
import { resetAdminDetail } from '../../../../features/admins/adminsSlice';
import { selectIsRunning } from '../../../../features/feedback/feedbackSlice';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

/** One plan switch, waiting only for its own change. */
const PlanSwitch = ({ field, switchKey, ...props }) => {
  const saving = useSelector(selectIsRunning(updateStaffAdminPlan, `${field}.${switchKey}`));
  return <Switch loading={saving} {...props} />;
};

/**
 * The switches of one group, or nothing.
 *
 * Both groups are JSON columns with a dict as their default, and some rows
 * carry an empty list instead — older plans, created before the default. An
 * empty card would read as "every channel is off", which is a different
 * statement from "this plan never declared any".
 *
 * @param {unknown} group
 * @returns {Array<[string, unknown]>}
 */
const entriesOf = (group) =>
  group && typeof group === 'object' && !Array.isArray(group) ? Object.entries(group) : [];

/**
 * One admin, and the plan that decides what its platform offers.
 *
 * The switches write straight to `admin_plan`: turning one on is turning a
 * feature on for that client and for every seller under it, so each one waits
 * for the server and shows what came back rather than what was clicked.
 */
export const AdminDetail = () => {
  const dispatch = useDispatch();
  const { adminId } = useParams();
  const { detail, loadingDetail, detailError } = useSelector((state) => state.admins);

  useEffect(() => {
    dispatch(fetchStaffAdminDetail(adminId));
    return () => {
      dispatch(resetAdminDetail());
    };
  }, [dispatch, adminId]);

  const handleToggle = (field, key, value) => {
    dispatch(updateStaffAdminPlan({ adminId, field, key, value }));
  };

  const reload = () => dispatch(fetchStaffAdminDetail(adminId));

  if (loadingDetail && !detail) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 size={28} className="text-ninja-green animate-spin" />
      </div>
    );
  }

  // A failed reload of the admin on screen leaves it there, under a strip.
  if (detailError && !detail) {
    return <ErrorState title="No se pudo cargar el admin" description={detailError} onRetry={reload} />;
  }

  if (!detail) return null;

  const fullName = `${detail.user?.first_name || ''} ${detail.user?.last_name || ''}`.trim();

  return (
    <div data-testid="admin-detail-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Operadores" to="/admins" current={fullName || 'Admin'} />

      {detailError && <StaleDataNotice testId="admin-detail-stale-error" error={detailError} onRetry={reload} />}

      <DetailHero
        media={<ShieldCheck size={40} strokeWidth={1.5} />}
        title={fullName || 'Sin nombre'}
        badges={
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-ninja-greenLight dark:bg-ninja-green/20 text-ninja-green border border-ninja-green/20">
            {detail.plan_name || 'Sin plan'}
          </span>
        }
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <Mail size={14} /> {detail.user?.email || '—'}
            </span>
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={14} /> RUT {detail.rut || '—'} &bull; ID interno: {detail.id}
            </span>
            <span className="flex items-center gap-1.5">
              <Globe size={14} /> {detail.country_fk?.name || '—'}
            </span>
            <span className="flex items-center gap-1.5 text-text-muted font-medium">
              <Calendar size={14} /> Creado el {formatDate(detail.created_at)} &bull; último acceso{' '}
              {formatDate(detail.user?.last_login)}
            </span>
          </>
        }
      />

      {detail.admin_plan ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {PLAN_GROUPS.map(({ field, title, labels }) => {
            const entries = entriesOf(detail.admin_plan[field]);

            return (
              <section
                key={field}
                data-testid={`plan-group-${field}`}
                className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-border">
                  <h2 className="font-bold text-ninja-dark dark:text-white">{title}</h2>
                </div>

                {entries.length === 0 ? (
                  <p className="p-5 text-sm text-text-secondary">
                    Este plan no declara ninguna capacidad en este grupo.
                  </p>
                ) : (
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {entries.map(([key, value]) => (
                      <PlanSwitch
                        key={key}
                        field={field}
                        switchKey={key}
                        checked={Boolean(value)}
                        onChange={(next) => handleToggle(field, key, next)}
                        label={labels[key] ?? key}
                        ariaLabel={labels[key] ?? key}
                        testId={`plan-switch-${field}-${key}`}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="bg-blue-50 dark:bg-blue-900/20 text-info px-5 py-3 rounded-2xl border border-blue-200 dark:border-blue-900 text-sm">
          Este admin no tiene un plan asociado, así que no hay capacidades que activar.
        </div>
      )}
    </div>
  );
};
